import dayjs from 'dayjs'
import { formatDate } from 'src/variable/constant'

const MAX_CELL_TEXT = 80

export const USER_LIST_API_FIELDS = [
	'id',
	'name',
	'email',
	'phone',
	'avatar',
	'address',
	'address_local',
	'invite_code',
	'version',
	'blocked_at',
	'unblocked_at',
	'is_verified',
	'is_regular',
	'created_at',
	'deleted_at',
]

const truncateText = (value: unknown, max = MAX_CELL_TEXT) => {
	const text = value == null ? '' : String(value).trim()
	if (!text) {
		return ''
	}
	return text.length > max ? `${text.slice(0, max)}…` : text
}

const formatRowDate = (value: unknown) => {
	if (value == null || value === '') {
		return null
	}

	const parsed = dayjs(value as string | number)
	return parsed.isValid() ? parsed.format(formatDate) : null
}

/** Who confirmed the phone owner before staff created the account in the CMS */
export type UserOwnerVerification = {
	channel: string
	verified_by_name: string
	verified_at: string
}

// The list may not send owner_verification at all; anything but an object is "not confirmed"
const mapOwnerVerification = (value: any): UserOwnerVerification | null => {
	if (!value || typeof value !== 'object') {
		return null
	}

	return {
		channel: truncateText(value.channel, 32),
		verified_by_name: truncateText(value.verified_by_name),
		verified_at: formatRowDate(value.verified_at) ?? '',
	}
}

/** Row shape for Users table — subset of API $all, safe for iPad setData */
export type UserTableRow = {
	no: number
	id: string
	avatar: string
	phone: string
	email: string
	name: string
	address: string
	address_local: string
	invite_code: string
	version: string | null
	blocked_at: unknown
	unblocked_at: unknown
	is_verified: boolean
	is_regular: boolean
	created_at: string
	deleted_at: string | null
	owner_verification: UserOwnerVerification | null
	/** UD-784: XP level, null while the server sends none (XP off) */
	xp_level: number | null
	/** UD-784: level 6 or more and no green tick yet */
	tick_suggested: boolean
}

/** Chỉ giữ field table cần — tránh spread $all (nested objects) gây crash iPad khi setData */
export const mapUserListRows = (
	rows: unknown[] | null | undefined,
	page: number,
	pageSize: number,
): UserTableRow[] =>
	(rows ?? []).map((item: any, index) => ({
		no: index + 1 + pageSize * (page - 1),
		id: String(item?.id ?? ''),
		avatar: truncateText(item?.avatar, 512),
		phone: truncateText(item?.phone),
		email: truncateText(item?.email),
		name: truncateText(item?.name),
		address: truncateText(item?.address),
		address_local: truncateText(item?.address_local),
		invite_code: truncateText(item?.invite_code, 32),
		version: item?.version != null ? truncateText(item.version, 32) : null,
		blocked_at: item?.blocked_at ?? null,
		unblocked_at: item?.unblocked_at ?? null,
		is_verified: !!item?.is_verified,
		is_regular: !!item?.is_regular,
		created_at: formatRowDate(item?.created_at) ?? '',
		deleted_at: formatRowDate(item?.deleted_at),
		owner_verification: mapOwnerVerification(item?.owner_verification),
		xp_level: Number.isFinite(Number(item?.xp_level)) && item?.xp_level != null ? Number(item.xp_level) : null,
		tick_suggested: item?.tick_suggested === true,
	}))
